import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { isPayPalV6Enabled } from 'utils/paypalV6Config';
import { Button, Spacing, Text, Modal } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import Header from 'components/Header';
import PaymentStatusTitle from 'components/purchase/PaymentStatusTitle';
import HelpContact from 'components/purchase/HelpContact';
import CardPaymentInfo from 'components/purchase/CardPaymentInfo';
import OxxoPaymentInfo from 'components/purchase/OxxoPaymentInfo';
import PaycashPaymentInfo from 'components/purchase/PaycashPaymentInfo';
import PurchasePricing from 'components/purchase/PurchasePricing';
import TicketItinerary from 'components/purchase/TicketItinerary';
import DotersPointsSummary from 'ui/atoms/DotersPointsSummary';
import EvertecPaymentInfo from 'components/purchase/EvertecPaymentInfo';
import EvertecPaymentDetails from 'components/purchase/EvertecPaymentDetails';
import CSSVariablesProvider from 'components/CSSVariablesProvider';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import LoadingWallet from 'components/LoadingWallet';
import YunoPaymentInfo from '../../YunoPaymentInfo';
import CashPaymentInfo from '../../CashPaymentInfo';
import BrandLogo from '../../../BrandLogo';
import TransferPaymentTicket from '../../TransferPaymentTicket';
import parsePurchaseStatus from '../../../../utils/parsePurchaseStatus';
import PaypalPaymentInfo from '../../PaypalPaymentInfo';
import BankDetails from '../../BankDetails';
import DownloadTicketsContainer from '../../DownloadTicketsContainer';
import { PurchaseCompleteAccountActivation } from '../../PurchaseCompleteAccountActivation';
import useReactNativeMessage from '../../../../hooks/useReactNativeMessage';
import PixPaymentInfo from '../../PixPaymentInfo';
import EfectyPaymentInfo from '../../EfectyPaymentInfo/EfectyPaymentInfo';
import wayIsOpenTicket from '../../../../utils/wayIsOpenTicket';
import EvaluateButton from '../../../../ui/atoms/EvaluateButton';
import PurchaseLayout from '../../../../ui/layouts/PurchaseLayout';
import 'styles/components/purchase/PurchasesLayout';
import NequiPaymentInfo from '../../NequiPaymentInfo';
import TapatioBanner from '../../../../ui/atoms/TapatioBanner';
import { tapatioBannerClickTracker } from '../../../../metrics/user-analytics/purchase';
import {
  getPurchaseDestinationName,
  getPurchaseOriginName,
} from '../../../../utils/purchasePlaceNames';
import getTapatioBannerHref from '../../../../utils/tapatioBanner';
import PaymentCardSummary from '../PaymentCardSummary';
import useSurveyFlagsOnCompletion from '../useSurveyFlagsOnCompletion';

/**
 * PurchaseComplete component handles the display and logic for the purchase completion process.
 * It manages different payment statuses and types, tracks the purchase, and displays relevant information
 * based on the purchase status.
 *
 * @param {Object} props - The properties object.
 * @param {boolean} props.isFetching - Indicates if the purchase data is being fetched.
 * @param {Function} props.getPurchase - Function to fetch the purchase data.
 * @param {Function} props.pollPurchaseComplete - Function to poll the purchase completion status.
 * @param {boolean} props.loaded - Indicates if the purchase data has been loaded.
 * @param {string} props.paymentEngine - The payment engine used for the purchase.
 * @param {string} props.paymentType - The type of payment used for the purchase.
 * @param {string} props.paymentProvider - The payment provider used for the purchase.
 * @param {string} props.purchaseStatus - The current status of the purchase.
 * @param {string} props.paymentStatus - The current status of the payment.
 * @param {number} props.paymentId - The ID of the payment.
 * @param {Function} props.confirmPaypalPayment - Function to confirm PayPal payment.
 * @param {string} props.token - The token associated with the purchase.
 * @param {boolean} props.trackPurchase - Indicates if the purchase should be tracked.
 * @param {Function} props.trackPurchaseHandler - Function to handle tracking the purchase.
 * @param {Function} props.refreshSurveyFlags - Rewrites the URL survey flags once the purchase completes.
 * @param {string} props.transferPaymentState - The state of the transfer payment.
 * @param {boolean} props.isOpenTicket - Indicates if the ticket is open.
 * @param {string} props.email - The email associated with the purchase.
 * @param {string} props.departsTicketPdfUrl - The URL for the departure ticket PDF.
 * @param {string} props.departsTransporterKey - The transporter key for the departure.
 * @param {string} props.returnTicketPdfUrl - The URL for the return ticket PDF.
 * @param {string} props.returnsTransporterKey - The transporter key for the return.
 * @param {string} props.departsReserveCode - The reserve code for departure.
 * @param {string} props.returnsReserveCode - The reserve code for return.
 * @param {boolean} props.isExchange - Indicates if the purchase is an exchange.
 * @param {Object} props.purchase - The purchase data object.
 * @param {Array} props.departsTickets - Array of departure tickets.
 * @param {Array} props.returnsTickets - Array of return tickets.
 * @param {string} props.cardBrand - Brand of the card used for the payment.
 * @param {string} props.cardNumber - Stored ending of the card used for the payment.
 * @param {string} props.purchaserFirstName - Purchaser first name.
 * @param {string} props.purchaserLastName - Purchaser last name.
 *
 * @returns {JSX.Element} The rendered PurchaseComplete component.
 */
const PurchaseComplete = ({
  isFetching,
  getPurchase,
  pollPurchaseComplete,
  loaded,
  paymentEngine,
  paymentType,
  paymentProvider,
  purchaseStatus,
  paymentStatus,
  paymentId,
  confirmPaypalPayment,
  token,
  trackPurchase,
  trackPurchaseHandler,
  refreshSurveyFlags,
  transferPaymentState,
  isOpenTicket,
  email,
  purchaserFirstName,
  purchaserLastName,
  departsTicketPdfUrl,
  departsTransporterKey,
  returnTicketPdfUrl,
  returnsTransporterKey,
  departsReserveCode,
  returnsReserveCode,
  isExchange,
  purchase,
  departsTickets,
  returnsTickets,
  cardBrand,
  cardNumber,
}) => {
  const {
    env,
    copies: { provider },
    features,
  } = useSelector((state) => state.whitelabelConfig);
  const history = useHistory();
  const [isTracked, setIsTracked] = useState(!trackPurchase);
  const { t } = useTranslation('purchase');
  const RNMessage = useReactNativeMessage();
  const { theme, brand } = useWhitelabelEnvs();
  const { FUNNEL_STYLE } = useWhitelabelFeatures();
  const tenantUsesRemoteTickets = features.REMOTE_TICKETS;
  const canDotersGenerate = Boolean(purchase.walletAccrualPoints || purchase.walletPointsUsed);
  const dotersAccrualNotSameUser =
    purchase.isDotersUser && !purchase.isDotersPassenger && !purchase.walletAccrualPoints;
  const isEvertecPayment = paymentEngine === 'evertec';
  const isEvertecPending = paymentStatus === 'attempt' && isEvertecPayment;
  const isPixPayment = paymentProvider === 'pix';
  const isPixPending = purchaseStatus === 'attempt' && isPixPayment;
  const tapatioBannerHref = getTapatioBannerHref(env);
  const isStripePending = purchaseStatus === 'attempt' && paymentEngine === 'stripe';

  const isYunoPending =
    (paymentStatus === 'pending' || paymentStatus === 'attempt') && paymentEngine === 'yuno';
  const hasConfirmedPaypalRef = useRef(false);

  useEffect(() => {
    if (!loaded) getPurchase(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-confirm PayPal V6 payment when user returns from redirect
  // Guard against duplicate calls with useRef to prevent race conditions
  useEffect(() => {
    if (
      loaded &&
      paymentType === 'paypal' &&
      purchaseStatus === 'attempt' &&
      paymentId &&
      isPayPalV6Enabled()
    ) {
      if (!hasConfirmedPaypalRef.current) {
        hasConfirmedPaypalRef.current = true;
        confirmPaypalPayment(token, paymentId);
      }
    }
  }, [loaded, paymentType, purchaseStatus, paymentId, token, confirmPaypalPayment]);

  useEffect(() => {
    if (
      paymentStatus === 'chargeback' ||
      purchaseStatus === 'chargeback' ||
      purchaseStatus === 'canceled'
    ) {
      history.push(`/payment/${token}/canceled`);
    }

    if (
      (loaded && purchaseStatus === 'pending' && paymentStatus === 'charged') ||
      isEvertecPending ||
      isYunoPending ||
      isPixPending ||
      isStripePending
    ) {
      pollPurchaseComplete(token, isPixPayment);
    }
    // when purchase complete remove lastPurchase token from storage
    // so no longer needed to call unlock seats
    if (loaded && purchaseStatus === 'completed' && paymentStatus === 'charged') {
      window.sessionStorage?.removeItem('lastPurchaseToken');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, loaded, purchaseStatus, paymentStatus, pollPurchaseComplete]);

  useEffect(() => {
    if (loaded && !isTracked) {
      setIsTracked(true);
      trackPurchaseHandler();
    }
  }, [isTracked, loaded, trackPurchaseHandler]);

  useSurveyFlagsOnCompletion({ trackPurchase, isTracked, purchaseStatus, refreshSurveyFlags });

  // Only for Mobile app webview
  useEffect(() => {
    // Verify if payment has succeed
    if (purchaseStatus === 'completed' && paymentStatus === 'charged') {
      RNMessage.post({ success: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseStatus, paymentStatus]);

  // Only for Mobile app webview
  useEffect(() => {
    // Verify if payment has succeed
    if (paymentType === 'paypal' && purchaseStatus === 'attempt') {
      RNMessage.post({ showPaypalPayment: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentType, purchaseStatus]);

  if (purchaseStatus === 'chargeback' || isFetching || trackPurchase)
    return <LoadingWallet showText={false} />;

  const isCompleted = purchaseStatus === 'completed';
  const isAttempt = purchaseStatus === 'attempt';
  const showTransferDetails = paymentType === 'transfer' && transferPaymentState === 'OK';

  const showCardPayment = !isCompleted && paymentType === 'credit_card' && !isEvertecPayment;
  const showPaypalPayment =
    (paymentType === 'paypal' || paymentType === 'third_party') && isAttempt;
  const showCashPayment = !isCompleted && paymentType === 'store' && paymentEngine === 'kushki';
  const showOxxoPayment = paymentType === 'store' && !isCompleted && paymentEngine === 'conekta';
  const showPaycashPayment = paymentType === 'store' && !isCompleted && paymentEngine === 'paycash';
  const showPixPaymentDetails = isPixPayment && !isCompleted;
  const isEfectyPayment = paymentProvider === 'efecty';
  const isNequiPayment = paymentProvider === 'nequi';
  const showEfectyPaymentDetails = isEfectyPayment && !isCompleted;
  const showNequiPaymentDetails = isNequiPayment && !isCompleted;
  // const displayPaymentStatus = !isPixPayment;
  let dotersStatus =
    purchaseStatus === 'attempt' || paymentStatus === 'pending' ? 'pending' : 'completed';
  dotersStatus =
    paymentStatus === 'failed' || purchaseStatus === 'canceled' || purchase.isExpired
      ? 'refunded'
      : dotersStatus;
  return (
    <div className="l-complete">
      <Header isFixed>
        <BrandLogo />
      </Header>
      <PaymentStatusTitle />
      <PurchaseLayout
        isFlat
        sidebar={
          <Spacing size="XL" vertical>
            <Spacing vertical size="S">
              <Text weight="bold">{t('title.check_trip_itinerary')}</Text>
              <Modal
                title={t('title.trip_details')}
                modalTrigger={<Button text={t('button.view_itinerary')} color="primary" />}
                responsiveSize="L"
              >
                <CSSVariablesProvider
                  theme={theme}
                  funnelStyle={FUNNEL_STYLE}
                  funnelStyleBrandVariation={brand.toUpperCase()}
                >
                  <div className="trip-summary-itinerary-purchase-complete">
                    <TicketItinerary showSeats={false} showTickets showDownloadTicketsStep />
                  </div>
                </CSSVariablesProvider>
              </Modal>
            </Spacing>

            <Spacing vertical>
              <Text size="L" weight="bold">
                {t(isExchange ? 'label.exchange_details' : 'label.purchase_details')}
              </Text>
              <PurchasePricing />
            </Spacing>
            {showTransferDetails && <TransferPaymentTicket />}
            {isEvertecPayment && <EvertecPaymentDetails />}
            <BankDetails />
          </Spacing>
        }
      >
        <Spacing vertical>
          {isCompleted && (
            <Spacing vertical size="L">
              <DownloadTicketsContainer
                hrefDeparture={departsTicketPdfUrl}
                hrefReturn={returnTicketPdfUrl}
                instructions={t('ticket_instructions_digital', { context: env.brand })}
                isDepartureOpen={wayIsOpenTicket(purchase.departs)}
                isReturnOpen={wayIsOpenTicket(purchase.returns)}
                status={parsePurchaseStatus(purchaseStatus, paymentType)}
                isOpenTicket={isOpenTicket}
                displayDownloadTicketsButton={!tenantUsesRemoteTickets}
                email={email}
                departsTransporterKey={departsTransporterKey}
                returnsTransporterKey={returnsTransporterKey}
                token={token}
                departsTickets={departsTickets}
                returnsTickets={returnsTickets}
                departsReserveCode={departsReserveCode}
                returnsReserveCode={returnsReserveCode}
              />
              <PurchaseCompleteAccountActivation
                accountId={purchase.accountId}
                email={email}
                firstName={purchaserFirstName}
                lastName={purchaserLastName}
                purchaseToken={token}
                isExchange={isExchange}
              />
              <div>
                <EvaluateButton />
              </div>
              {/* {isOpenTicket || isHybridTrip && <ExchangeCTA />} TODO: Removes comment after the mixed ticket branch gets merged */}
            </Spacing>
          )}
          <PaymentCardSummary
            cardBrand={cardBrand}
            cardNumber={cardNumber}
            paymentType={paymentType}
          />
          {showCardPayment && <CardPaymentInfo />}
          {showCashPayment && <CashPaymentInfo />}
          {showOxxoPayment && <OxxoPaymentInfo />}
          {showPaypalPayment && <PaypalPaymentInfo />}
          {showPaycashPayment && <PaycashPaymentInfo />}
          {showPixPaymentDetails && <PixPaymentInfo />}
          {showEfectyPaymentDetails && <EfectyPaymentInfo />}
          {isEvertecPending && <EvertecPaymentInfo />}
          {isYunoPending && <YunoPaymentInfo />}
          {showNequiPaymentDetails && <NequiPaymentInfo />}
          <Spacing vertical />
          {(canDotersGenerate || dotersAccrualNotSameUser) && (
            <DotersPointsSummary
              dotersUsed={purchase.walletPointsUsed}
              dotersEarned={purchase.walletAccrualPoints}
              status={dotersStatus}
              isFirstPassengerNotAccountHolder={dotersAccrualNotSameUser}
            />
          )}
          <TapatioBanner
            href={tapatioBannerHref}
            onClick={() =>
              tapatioBannerClickTracker({
                token,
                brand: env.brand,
                url: tapatioBannerHref,
                origin: getPurchaseOriginName(purchase),
                destination: getPurchaseDestinationName(purchase),
              })
            }
          />
        </Spacing>

        <HelpContact
          email={provider.email}
          phones={provider.phones || []}
          invoiceUrl={provider.invoiceUrl}
          whatsapp={provider.whatsapp || []}
        />
      </PurchaseLayout>
    </div>
  );
};

PurchaseComplete.propTypes = {
  getPurchase: PropTypes.func.isRequired,
  pollPurchaseComplete: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  loaded: PropTypes.bool.isRequired,
  paymentEngine: PropTypes.string,
  paymentType: PropTypes.string,
  paymentProvider: PropTypes.string,
  purchaseStatus: PropTypes.string,
  paymentStatus: PropTypes.string,
  paymentId: PropTypes.number,
  confirmPaypalPayment: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  trackPurchase: PropTypes.bool.isRequired,
  trackPurchaseHandler: PropTypes.func.isRequired,
  refreshSurveyFlags: PropTypes.func.isRequired,
  transferPaymentState: PropTypes.string,
  isOpenTicket: PropTypes.bool.isRequired,
  email: PropTypes.string,
  purchaserFirstName: PropTypes.string,
  purchaserLastName: PropTypes.string,
  departsTicketPdfUrl: PropTypes.string,
  departsTransporterKey: PropTypes.string,
  returnTicketPdfUrl: PropTypes.string,
  returnsTransporterKey: PropTypes.string,
  departsReserveCode: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  returnsReserveCode: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  cardBrand: PropTypes.string,
  cardNumber: PropTypes.string,
  purchase: PropTypes.object,
  isExchange: PropTypes.bool,
  departsTickets: PropTypes.array,
  returnsTickets: PropTypes.array,
};

PurchaseComplete.defaultProps = {
  paymentEngine: null,
  transferPaymentState: null,
  departsTicketPdfUrl: '',
  departsTransporterKey: '',
  returnTicketPdfUrl: '',
  returnsTransporterKey: '',
  departsTickets: [],
  returnsTickets: [],
  departsReserveCode: null,
  returnsReserveCode: null,
};

export default PurchaseComplete;
